// src/utils/passwordRules.ts
// Avaliação client-side dos requisitos de senha, SÓ para UX (checklist ao vivo
// "espelho"). A validação autoritativa é do backend #58 (ADR-040): o front nunca
// bloqueia o submit por sua própria conta além do óbvio — o texto de erro exibido
// ao usuário vem sempre do backend (`padrao_exigido` no 403, ou `mensagem`/`error`
// nas respostas de troca). Esta função só antecipa o feedback enquanto digita.
//
// Regras espelhadas do backend (issue #27 §5):
//  - mínimo 8 caracteres;
//  - ao menos uma letra, um número e um caractere especial — "especial" é
//    QUALQUER caractere que não seja letra nem número (inclui espaço, |, ~, €…);
//  - máximo de 72 bytes (limite do algoritmo de hash).
// Não valida lista de senhas comuns nem igualdade a usuário/e-mail — esses ficam
// exclusivamente no backend (o front não tem a lista nem o contexto).

export interface PasswordRule {
  id: 'min8' | 'letter' | 'number' | 'special';
  label: string;
  met: boolean;
}

export interface PasswordEvaluation {
  rules: PasswordRule[];
  withinMaxBytes: boolean; // ≤ 72 bytes — guard, não exibido como item positivo
  allMet: boolean; // todas as rules + withinMaxBytes
}

const MAX_BYTES = 72;

// "Especial" = qualquer caractere que não seja letra (\p{L}) nem número (\p{N}).
// Partição limpa: letra ∪ número ∪ especial cobre todo caractere, alinhado à
// definição do backend.
const HAS_LETTER = /\p{L}/u;
const HAS_NUMBER = /\p{N}/u;
const HAS_SPECIAL = /[^\p{L}\p{N}]/u;

function byteLength(senha: string): number {
  return new TextEncoder().encode(senha).length;
}

export function evaluatePassword(senha: string): PasswordEvaluation {
  const rules: PasswordRule[] = [
    { id: 'min8', label: 'Mínimo de 8 caracteres', met: senha.length >= 8 },
    { id: 'letter', label: 'Ao menos uma letra', met: HAS_LETTER.test(senha) },
    { id: 'number', label: 'Ao menos um número', met: HAS_NUMBER.test(senha) },
    {
      id: 'special',
      label: 'Ao menos um caractere especial',
      met: HAS_SPECIAL.test(senha),
    },
  ];
  const withinMaxBytes = byteLength(senha) <= MAX_BYTES;
  const allMet = rules.every((r) => r.met) && withinMaxBytes;
  return { rules, withinMaxBytes, allMet };
}
